import {
  forwardRef,
  useRef,
  type PropsWithChildren,
  createContext,
  useState,
  useId,
  useContext,
  useEffect,
  useCallback,
  useLayoutEffect,
} from 'react';
import { useResizeObserver } from 'usehooks-ts';
import { cn } from '@/lib/cn';
import { uniq } from 'lodash';

type AutoHeightContainerContextType = {
  notifyAdd: (id: string) => void;
  notifyRemove: (id: string) => void;
};

const AutoHeightContainerContext =
  createContext<AutoHeightContainerContextType | null>(null);

export const AutoHeightContainer = forwardRef<
  HTMLDivElement,
  PropsWithChildren<{
    className?: string;
    childContainerClassName?: string;
    animateWidth?: boolean;
  }>
>(
  (
    { children, className, childContainerClassName, animateWidth = false },
    ref
  ) => {
    const [isInitalRender, setIsInitalRender] = useState(true);
    const selfId = useId();
    const [childrenAutoContainers, setChildrenAutoContainers] = useState<
      string[]
    >([]);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [batch, setBatch] = useState<{ add: string[]; remove: string[] }>({
      add: [],
      remove: [],
    });

    const internalMainRef = useRef<HTMLDivElement | null>(null);
    const childRef = useRef<HTMLDivElement>(null);
    const childSize = useResizeObserver({
      ref: childRef as any,
      // Will include the child's border and padding in calculating the size
      box: 'border-box',
    });

    const parentCtx = useContext(AutoHeightContainerContext);

    const notifyAdd = useCallback(
      (id: string) => {
        if (
          isTransitioning ||
          (!isInitalRender && childrenAutoContainers.length === 0)
        ) {
          // filter out if id is added to remove batch then added again before
          // process.
          setBatch((batch) => ({
            remove: batch.remove.filter((removeId) => removeId !== id),
            add: [...batch.add, id],
          }));
        } else {
          setChildrenAutoContainers((state) => [...state, id]);
        }
      },
      [isTransitioning, isInitalRender, childrenAutoContainers.length]
    );

    const notifyRemove = useCallback(
      (id: string) => {
        if (isTransitioning) {
          setBatch((batch) => ({
            // filter out if id is added to add batch then removed before
            // process. E.g. component is removed from dom before processbatch is
            // called
            add: batch.add.filter((addId) => addId !== id),
            remove: [...batch.remove, id],
          }));
        } else {
          const newState = [...childrenAutoContainers].filter(
            (listId) => listId !== id
          );
          setChildrenAutoContainers(newState);
        }
      },
      [
        isTransitioning,
        childrenAutoContainers,
        setBatch,
        setChildrenAutoContainers,
      ]
    );

    const processBatch = useCallback(() => {
      let internalNewState = [...childrenAutoContainers];
      if (batch.add.length > 0) {
        internalNewState.push(...uniq(batch.add));
      }
      if (batch.remove.length > 0) {
        internalNewState = internalNewState.filter(
          (val) => !batch.remove.includes(val)
        );
      }

      // clear batch
      setBatch({ add: [], remove: [] });

      setChildrenAutoContainers(internalNewState);
    }, [batch.add, batch.remove, childrenAutoContainers]);

    // Notify before unmouting happens to allow parent to animate when removed
    // if no other animation container siblings
    useLayoutEffect(() => {
      if (parentCtx) {
        return () => {
          parentCtx.notifyRemove(selfId);
        };
      }
      //we only want to run the effect on mount and unmount
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // notify once rendered to allow parent to animate when component is added
    // if no other animation container siblings
    useEffect(() => {
      if (parentCtx) {
        parentCtx.notifyAdd(selfId);
      }
      // passing parentCtx causes render loop
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selfId]);

    useEffect(() => {
      const currentRef = internalMainRef.current;
      if (!currentRef) return;

      setIsInitalRender(false);

      const transitionStartHandler = (ev: TransitionEvent) => {
        if (ev.target === currentRef) {
          setIsTransitioning(true);
        }
      };

      const transitionEndHandler = (ev: TransitionEvent) => {
        if (ev.target === currentRef) {
          setIsTransitioning(false);
          processBatch();
        }
      };

      currentRef.addEventListener('transitionstart', transitionStartHandler);
      currentRef.addEventListener('transitionend', transitionEndHandler);
      currentRef.addEventListener('transitioncancel', transitionEndHandler);

      return () => {
        currentRef.removeEventListener(
          'transitionstart',
          transitionStartHandler
        );
        currentRef.removeEventListener('transitionend', transitionEndHandler);
      };
    }, [processBatch]);

    const isDisabledByChild = childrenAutoContainers.length > 0;

    return (
      <AutoHeightContainerContext.Provider value={{ notifyRemove, notifyAdd }}>
        <div
          ref={(el) => {
            if (typeof ref === 'function') {
              ref(el);
            } else if (ref) {
              ref.current = el;
            }
            internalMainRef.current = el;
          }}
          style={
            isDisabledByChild
              ? undefined
              : {
                  height: childSize.height,
                  width: animateWidth ? childSize.width : undefined,
                }
          }
          className={cn(
            // box-content is the key to handle border and padding on this element
            // when setting height and width to child height and width
            'box-content overflow-hidden transition-[height_150ms,width_150ms] will-change-transform',
            className
          )}
        >
          <div ref={childRef} className={childContainerClassName}>
            {children}
          </div>
        </div>
      </AutoHeightContainerContext.Provider>
    );
  }
);

AutoHeightContainer.displayName = 'AutoHeightContainer';
